import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Typography, 
  Box, 
  AppBar, 
  Toolbar, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Avatar, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  TextField,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Notifications, 
  AccountCircle,
  Dashboard as DashboardIcon, 
  Call as CallIcon, 
  PhoneCallback as PhoneCallbackIcon,
  BarChart as AnalyticsIcon,
  Settings as SettingsIcon,
  Report as ReportIcon,
  ExitToApp as LogoutIcon,
  PhoneForwarded as OutboundIcon,
  CloudUpload as UploadIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Science as TestIcon,
  Feedback as FeedbackIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  BatchPrediction as BatchesIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService, organizationService, batchesService } from '../utils/api';
import { COMPANY_LOGO, COMPANY_NAME } from '../config';

function Outbound() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);

  // Organization state
  const [currentOrganization, setCurrentOrganization] = useState(null);

  // Check user role for access control
  const userRole = authService.getUserRole();
  const canAccessOutbound = userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator';

  // Redirect if user doesn't have access
  React.useEffect(() => {
    if (!canAccessOutbound) {
      navigate('/dashboard');
    }
  }, [canAccessOutbound, navigate]);

  // Load current organization on component mount
  useEffect(() => {
    const org = organizationService.getSelectedOrganization();
    setCurrentOrganization(org);
  }, []);

  // Load existing batches to show occupied slots
  useEffect(() => {
    const loadBatches = async () => {
      try {
        setBatchesLoading(true);
        const data = await batchesService.getBatches();
        setBatches(data.results || []);
      } catch (err) {
        console.error('Error loading batches', err);
      } finally {
        setBatchesLoading(false);
      }
    };
    loadBatches();
  }, []);

  // Call scheduler state
  const [selectedFile, setSelectedFile] = useState(null);
  const [earliestCallTime, setEarliestCallTime] = useState('');
  const [batchName, setBatchName] = useState('');
  const [endTime, setEndTime] = useState('');
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedulerMessage, setSchedulerMessage] = useState('');
  const [schedulerError, setSchedulerError] = useState('');
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  // Call scheduler handlers
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        setSchedulerError('');
      } else {
        setSchedulerError('Please select a valid Excel file (.xlsx or .xls)');
        setSelectedFile(null);
      }
    }
  };

  const convertISTToGMT = (istDateTime) => {
    if (!istDateTime) return '';
    const date = new Date(istDateTime);
    return date.toISOString(); // keep Z for clarity
  };

  const handleEarliestCallTimeChange = (e) => {
    const selectedTime = e.target.value;
    setEarliestCallTime(selectedTime);
  };

  const handleEndTimeChange = (e) => {
    const selectedTime = e.target.value;
    setEndTime(selectedTime);
  };

  const formatWindow = (start, eta) => {
    if (!start) return 'N/A';
    const s = new Date(start).toLocaleString();
    const e = eta ? new Date(eta).toLocaleString() : 'N/A';
    return `${s} → ${e}`;
  };

  const handleScheduleCalls = async () => {
    if (!selectedFile || !earliestCallTime || !batchName) {
      setSchedulerError('Please fill all required fields');
      return;
    }

    try {
      setSchedulerLoading(true);
      setSchedulerError('');
      
      const earliestGMT = convertISTToGMT(earliestCallTime);
      
      // we need task count for availability calc
      // The backend calculates from the file; for pre-check we skip.
      const formData = new FormData();
      formData.append('start_time', earliestGMT);
      formData.append('batch_name', batchName);
      formData.append('file', selectedFile);
      if (endTime) {
        const endGMT = convertISTToGMT(endTime);
        formData.append('end_time', endGMT);
      }

      const response = await batchesService.createBatch(formData);
      const etaText = response?.eta_time ? ` ETA end: ${new Date(response.eta_time).toLocaleString()}` : '';
      setSchedulerMessage(response.message || `Call batch scheduled successfully!${etaText}`);
      // Reset form
      setSelectedFile(null);
      setEarliestCallTime('');
      setBatchName('');
      // Reset file input
      const fileInput = document.getElementById('excel-file-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      let errMsg = error?.message || error?.detail || 'Failed to schedule call batch';
      const conflicts = error?.conflicts || error?.detail?.conflicts;
      if (conflicts && Array.isArray(conflicts) && conflicts.length > 0) {
        const first = conflicts[0];
        errMsg = `Requested slot overlaps with existing batch (id ${first.id}) from ${new Date(first.start_time).toLocaleString()} to ${new Date(first.eta_time).toLocaleString()}`;
      }
      setSchedulerError(errMsg);
    } finally {
      setSchedulerLoading(false);
    }
  };

  const handleCloseSchedulerMessage = () => {
    setSchedulerMessage('');
  };

  const handleCloseSchedulerError = () => {
    setSchedulerError('');
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrganization = () => {
    navigate('/organizations');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
    { text: 'Call Logs', icon: <CallIcon />, path: '/call-logs' },
    ...(canAccessOutbound && currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Outbound Calls', icon: <OutboundIcon />, path: '/outbound' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Batches', icon: <BatchesIcon />, path: '/batches' }] : []),
    ...(userRole !== 'viewer' ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    ...(userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator' ? [{ text: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Test', icon: <TestIcon />, path: '/test' }] : []),
  ];

  const drawerWidth = 240;

  const drawer = (
    <Box sx={{ 
      width: drawerWidth, 
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
      borderRight: '1px solid rgba(102, 117, 140, 0.12)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        pt: '12px',
        pb: '12px',
        px: 2,
        borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
        background: '#2541b2',
        color: 'white',
        position: 'relative'
      }}>
        {/* Side-by-side logos */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          justifyContent: 'center',
          width: '100%'
        }}>
          <Box
            component="img"
            src="/telly_logo.png"
            alt="TellyFlow"
            sx={{
              width: 'auto',
              height: '32px',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)'
            }}
          />
          <Box sx={{ 
            width: '1px', 
            height: '24px', 
            backgroundColor: 'rgba(255, 255, 255, 0.3)' 
          }} />
          <Box
            sx={{
              width: '60px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {COMPANY_LOGO && (
              <Box
                component="img"
                src={COMPANY_LOGO}
                alt={`${COMPANY_NAME || 'Company'} Logo`}
                sx={{
                  height: '60px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {drawerItems.map((item) => {
            const isActive = item.text === 'Outbound Calls';
            return (
              <ListItem 
                button 
                key={item.text} 
                onClick={() => navigate(item.path)}
                sx={{ 
                  mb: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive ? 'rgba(67, 97, 238, 0.08)' : 'transparent',
                  border: isActive ? '1px solid rgba(67, 97, 238, 0.2)' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(67, 97, 238, 0.12)' : 'rgba(102, 117, 140, 0.04)',
                    transform: 'translateX(2px)',
                    boxShadow: isActive ? '0 4px 12px rgba(67, 97, 238, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  },
                  '&:active': {
                    transform: 'translateX(1px)'
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    color: isActive ? 'primary.main' : 'text.primary',
                    transition: 'all 0.2s ease-in-out'
                  }} 
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(102, 117, 140, 0.08)',
        background: 'rgba(102, 117, 140, 0.02)'
      }}>
        <ListItem 
          button 
          onClick={handleLogout} 
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(239, 71, 111, 0.08)',
              transform: 'translateX(2px)',
              boxShadow: '0 2px 8px rgba(239, 71, 111, 0.12)'
            },
            '&:active': {
              transform: 'translateX(1px)'
            }
          }}
        >
          <ListItemIcon 
            sx={{ 
              minWidth: 40,
              color: 'text.secondary',
              transition: 'color 0.2s ease-in-out',
              '.MuiListItem-root:hover &': {
                color: 'error.main'
              }
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
              transition: 'color 0.2s ease-in-out'
            }} 
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Sidebar drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerOpen && !isMobile ? drawerWidth : 0}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* App Bar */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Outbound Calls
            </Typography>
            
            {/* Organization Switcher Button */}
            {currentOrganization && (
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                endIcon={<SwitchIcon />}
                onClick={handleSwitchOrganization}
                sx={{
                  mr: 2,
                  borderColor: 'rgba(67, 97, 238, 0.3)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(67, 97, 238, 0.05)',
                    color: 'primary.main'
                  }
                }}
              >
                {currentOrganization.name}
              </Button>
            )}
            
            <IconButton color="inherit" onClick={handleOpenMenu}>
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Outbound Content */}
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
          {/* Welcome section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              Schedule Outbound Calls
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Upload an Excel file with phone numbers and schedule batch calls within an hour
            </Typography>
          </Box>

          {/* Call Scheduler Section */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <ScheduleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Batch Call Scheduler
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload your Excel file and set the initiation time for automated calls
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* File Upload */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, border: '2px dashed', borderColor: 'divider', textAlign: 'center', cursor: 'pointer' }}>
                    <input 
                      type="file" 
                      id="excel-file-input"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="excel-file-input" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                      <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        {selectedFile ? selectedFile.name : 'Upload Excel File'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select an Excel file containing phone_number and name column
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Supported formats: .xlsx, .xls
                      </Typography>
                    </label>
                  </Paper>
                </Grid>

                {/* Date Time Pickers */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      label="Call Initiation Time (IST)"
                      type="datetime-local"
                      value={earliestCallTime}
                      onChange={handleEarliestCallTimeChange}
                      fullWidth
                      helperText="Select the start time to begin this batch (IST timezone)"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: new Date().toISOString().slice(0, 16)
                      }}
                    />

                    <TextField
                      label="Batch Name"
                      value={batchName}
                      onChange={(e) => setBatchName(e.target.value)}
                      fullWidth
                      required
                      helperText="Enter a name for this call batch"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      label="Optional End Time (IST)"
                      type="datetime-local"
                      value={endTime}
                      onChange={handleEndTimeChange}
                      fullWidth
                      helperText="If set, defines latest time to finish this batch"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: earliestCallTime || new Date().toISOString().slice(0, 16)
                      }}
                    />

                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={handleScheduleCalls}
                      disabled={schedulerLoading || !selectedFile || !earliestCallTime || !batchName}
                      sx={{ mt: 2 }}
                    >
                      {schedulerLoading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Scheduling...
                        </>
                      ) : (
                        <>
                          <ScheduleIcon sx={{ mr: 1 }} />
                          Schedule Calls
                        </>
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {/* Occupied slots preview */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Upcoming / Scheduled Batches (occupied windows)
                </Typography>
                {batchesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} /> <Typography variant="body2">Loading slots...</Typography>
                  </Box>
                ) : batches.filter(b => ['scheduled', 'started'].includes(b.status)).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No scheduled batches.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {batches
                      .filter(b => ['scheduled', 'started'].includes(b.status))
                      .map(b => (
                        <Box key={b.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.name || 'Batch'}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatWindow(b.start_time || b.start_from, b.eta_time || b.end_till)}</Typography>
                          </Box>
                          <Chip label={b.status} size="small" color={b.status === 'started' ? 'warning' : 'info'} sx={{ textTransform: 'capitalize' }} />
                        </Box>
                      ))}
                  </Box>
                )}
              </Box>

              {/* Help Text */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Instructions:
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>Upload an Excel file (.xlsx or .xls) containing a column named "phone_number"</li>
                    <li>Select the call initiation time (in IST)</li>
                    <li>Calls will happen within the hour after the initiation time</li>
                  </ul>
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Success/Error Messages */}
          <Snackbar 
            open={!!schedulerMessage}
            autoHideDuration={6000}
            onClose={handleCloseSchedulerMessage}
          >
            <Alert onClose={handleCloseSchedulerMessage} severity="success" sx={{ width: '100%' }}>
              {schedulerMessage}
            </Alert>
          </Snackbar>

          <Snackbar 
            open={!!schedulerError}
            autoHideDuration={6000}
            onClose={handleCloseSchedulerError}
          >
            <Alert onClose={handleCloseSchedulerError} severity="error" sx={{ width: '100%' }}>
              {schedulerError}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </Box>
  );
}

export default Outbound; 